// Pure decision core for "is the browser APPLICATION Safari itself"
// (df-b3c9fd82289e). Kept Angular- and DOM-free so the whole rule unit-tests
// under plain Node; `isSafariBrowserApp()` in environments/environment-helpers
// is the one-line wrapper that reads the browser and calls this. See
// design-docs/scanner-shared-components.md, "Where the camera permission lives".
//
// It answers a different question from `isBrowserSafari()` beside that wrapper,
// which asks whether the ENGINE is WebKit — true of every browser on an iPhone
// or iPad, because Apple requires them all to use WebKit.

/**
 * Whether the browser application is Safari, from what the browser calls itself.
 *
 * `isAppleMobileDevice` is passed in rather than worked out here because
 * recognising an iPhone or iPad needs the DOM as well as the user agent: an iPad
 * asking for the desktop site sends a Mac user agent and is told apart by its
 * touch support. Do not "simplify" it away.
 */
export function isSafariAppUserAgent(userAgent: string, isAppleMobileDevice: boolean): boolean {
  // if the engine is not WebKit-without-Chrome, this is some other browser
  // entirely (Chrome-based browsers say "Safari" in their user agent too)
  if (!userAgent.includes('Safari') || userAgent.includes('Chrome') || userAgent.includes('Chromium')) {
    return false;
  }
  // if this is neither an iPhone or iPad nor a Mac, it is another WebKit browser
  // wearing Safari's name — GNOME Web on Linux says "Safari" as well, and has
  // none of the settings screens the Safari instructions send people to
  if (!isAppleMobileDevice && !userAgent.includes('Macintosh')) {
    return false;
  }
  // if this is an Apple device, the browsers that are NOT Safari announce
  // themselves with a token of their own: Chrome CriOS, Firefox FxiOS, Edge
  // EdgiOS, Opera OPiOS. Safari's own user agent carries no such token. A
  // browser that copies Safari's user agent outright — Brave on iOS does — is
  // not distinguishable and is reported as Safari.
  return !/[A-Za-z]+iOS\//.test(userAgent);
}
